import React from 'react';
import PropTypes from 'prop-types';
import Divider from '@adsk/alloy-react-divider';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import Banner from '@adsk/alloy-react-banner';
import Tooltip from '@adsk/alloy-react-tooltip';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-helpers';
import { SERVICE_ACCESSES } from '@common/constants';
import { fieldStyle } from '../../styles';
import ProductsAccesses from '../ProductsAccesses';
import {
  noPurchasedProductsTextStyle,
  productsLabelStyle,
  tooltipIconStyle,
} from './styles';

const ProductsSection = ({
  accountName,
  isAccountAdmin,
  isTurningOffDocs,
  onProductAccessChange,
  onProductsBannerClose,
  onTurnOffDocsConfirmationClick,
  permissionsEditState,
  purchasedProducts,
  selectedAccessLevel,
  setExpandedProduct,
  setTurnOffDocsConfirmation,
  showProductsBanner,
  showProductsSectionRedesign,
  showTurnOffDocsConfirmation,
  unpurchansedProducts,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  return (
    <>
      {showProductsBanner && (
        <Banner
          data-testid="ProductsSection_Banner_TurnOnProducts"
          inline
          message={T.translate('TURN_ON_PRODUCTS_BANNER')}
          onClose={onProductsBannerClose}
          style={{ marginTop: '20px' }}
        />
      )}
      <div css={fieldStyle} data-testid="Purchased_Products">
        <div css={productsLabelStyle}>
          {showProductsSectionRedesign ? (
            <div>{T.translate('PURCHASED_PRODUCTS')}</div>
          ) : (
            <>
              <div>{T.translate('PURCHASED_PRODUCTS_OLD')}</div>
              <Tooltip
                shouldWrapChildren={false}
                content={T.translate('PURCHASED_PRODUCTS_TOOLTIP')}
              >
                <AlertInformationIcon size={16} style={tooltipIconStyle} />
              </Tooltip>
            </>
          )}
        </div>
        {purchasedProducts.length > 0 ? (
          <ProductsAccesses
            onProductAccessChange={onProductAccessChange}
            permissionsEditState={permissionsEditState}
            productsPermissions={purchasedProducts}
            selectedAccessLevel={selectedAccessLevel.value}
            setExpandedProduct={setExpandedProduct}
            showProductsSectionRedesign={showProductsSectionRedesign}
          />
        ) : (
          <div css={noPurchasedProductsTextStyle}>
            {T.translate('NO_PURCHASED_PRODUCTS')}
          </div>
        )}
      </div>
      {!showProductsSectionRedesign && (
        <Divider style={{ marginTop: '12px' }} />
      )}
      {(!showProductsSectionRedesign ||
        (showProductsSectionRedesign &&
          isAccountAdmin &&
          unpurchansedProducts.length > 0)) && (
        <div css={fieldStyle} data-testid="Not_Purchased_Products">
          <div css={productsLabelStyle}>
            <div>
              {showProductsSectionRedesign
                ? T.translate('NOT_PURCHASED')
                : T.translate('NOT_PURCHASED_OLD')}
            </div>
            <Tooltip
              content={
                showProductsSectionRedesign
                  ? T.translate('NOT_PURCHASED_PRODUCTS_TOOLTIP', {
                      accountName,
                    })
                  : T.translate('NOT_PURCHASED_PRODUCTS_TOOLTIP_OLD')
              }
              shouldWrapChildren={false}
            >
              <AlertInformationIcon
                data-testid="ProductsSection_Icon_NotPurchasedProducts"
                size={16}
                style={tooltipIconStyle}
              />
            </Tooltip>
          </div>
          <ProductsAccesses
            onProductAccessChange={onProductAccessChange}
            permissionsEditState={permissionsEditState}
            productsPermissions={unpurchansedProducts}
            selectedAccessLevel={selectedAccessLevel?.value}
            setExpandedProduct={setExpandedProduct}
            showProductsSectionRedesign={showProductsSectionRedesign}
          />
        </div>
      )}
      <ModalConfirmation
        cancelButtonLabel={T.translate('GO_BACK')}
        confirmationButtonLabel={T.translate('TURN_OFF')}
        data-testid="TurnOffDocs_ModalConfirmation"
        disableModalExit
        isConfirmationButtonAlert
        isOnConfirmInProgress={isTurningOffDocs}
        message={
          selectedAccessLevel?.value === SERVICE_ACCESSES.ADMIN
            ? T.translate('TURN_OFF_DOCS_CONFIRMATION_BODY_ADMIN')
            : T.translate('TURN_OFF_DOCS_CONFIRMATION_BODY_MEMBER')
        }
        onClose={() => setTurnOffDocsConfirmation(false)}
        onConfirm={onTurnOffDocsConfirmationClick}
        show={showTurnOffDocsConfirmation}
        title={T.translate('TURN_OFF_DOCS_CONFIRMATION_TITLE')}
      />
    </>
  );
};

ProductsSection.propTypes = {
  accountName: PropTypes.string.isRequired,
  isAccountAdmin: PropTypes.bool.isRequired,
  isTurningOffDocs: PropTypes.bool.isRequired,
  onProductAccessChange: PropTypes.func.isRequired,
  onProductsBannerClose: PropTypes.func.isRequired,
  onTurnOffDocsConfirmationClick: PropTypes.func.isRequired,
  permissionsEditState: PropTypes.bool.isRequired,
  purchasedProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedAccessLevel: PropTypes.objectOf(PropTypes.string).isRequired,
  setExpandedProduct: PropTypes.func.isRequired,
  setTurnOffDocsConfirmation: PropTypes.func.isRequired,
  showProductsBanner: PropTypes.bool.isRequired,
  showProductsSectionRedesign: PropTypes.bool.isRequired,
  showTurnOffDocsConfirmation: PropTypes.bool.isRequired,
  unpurchansedProducts: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default ProductsSection;
